/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import {
  AiFillStar,
  AiOutlineCaretDown,
  AiOutlineFileImage,
  AiOutlineCloudUpload,
} from "react-icons/ai";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { default as CustomInput } from "react-phone-input-2";
import { MdOutlineCheckCircle, MdOutlineClose } from "react-icons/md";
import { countriesList, map } from "./data";
import { getCountry } from "../../../middlewares/sysNavigation/SysNavigation";
import { getOneLevel } from "../../../middlewares/breadCrumb/BreadCrumb";
import JoditEditor from "jodit-react";

export const Codes = [
  { country_short: "", country_code: "Select", value: "" },
  { country_short: "cd", country_code: "CD +243", value: "243" },
  { country_short: "ug", country_code: "UG +256", value: "256" },
];

/** =========================
 * InputField (clean & pro)
 * ========================= */
export const InputField = ({ props }) => {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={props.id}
        className="flex items-center text-xs font-semibold text-gray-800"
      >
        <span className="capitalize">{props.title}</span>
        {props.required === true ? (
          <AiFillStar className="ml-1 h-3 w-3 text-red-600" />
        ) : null}
      </label>
      <input
        id={props.id}
        type={props.type}
        className={[
          "w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900",
          "placeholder:text-xs placeholder:text-gray-400",
          "shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200",
          props.disabled ? "opacity-60 cursor-not-allowed" : "",
        ].join(" ")}
        defaultValue={props.default}
        disabled={props.disabled}
        onClick={props.onClick}
        onChange={props.onChange}
        value={props.value}
        placeholder={props?.placeholder || props.title}
      />
    </div>
  );
};

/** =========================
 * PasswordField (show/hide) — design-only improvements
 * ========================= */
export const PasswordField = ({
  value,
  required,
  disabled,
  onChange,
  isConfirmPassword,
}) => {
  const [show, setShow] = useState(false);
  const label = !isConfirmPassword ? "Password" : "Confirm Password";

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={label}
        className="flex items-center gap-1 text-xs font-semibold text-gray-800"
      >
        <span>{label}</span>
        {required ? <AiFillStar className="h-3 w-3 text-red-600" /> : null}
      </label>

      <div
        className={[
          "relative rounded-xl ring-1 ring-gray-200 transition",
          "focus-within:ring-2 focus-within:ring-emerald-500",
          disabled ? "opacity-60" : "",
        ].join(" ")}
      >
        <input
          id={label}
          disabled={disabled}
          onChange={onChange}
          placeholder={label}
          value={value ? value : ""}
          type={show ? "text" : "password"}
          autoComplete={isConfirmPassword ? "new-password" : "current-password"}
          className={[
            "w-full rounded-xl bg-white px-3 py-2.5 text-sm text-gray-900",
            "placeholder:text-xs placeholder:text-gray-400",
            "outline-none border-0 shadow-sm",
            "pr-12",
          ].join(" ")}
        />

        <button
          type="button"
          onClick={() => setShow(!show)}
          className={[
            "absolute right-2 top-1/2 -translate-y-1/2",
            "inline-flex h-9 w-9 items-center justify-center",
            "rounded-lg text-gray-500 hover:text-gray-700",
            "transition focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70",
            "bg-white/80 backdrop-blur",
          ].join(" ")}
          aria-label={show ? "Hide password" : "Show password"}
        >
          <span className="text-base leading-none">
            {show ? <FaEyeSlash /> : <FaEye />}
          </span>
        </button>
      </div>
    </div>
  );
};

/** =========================
 * Select (classic)
 * ========================= */
export const Select = ({ props }) => {
  return (
    <div className="flex flex-col space-y-1.5">
      <label
        htmlFor={props?.id}
        className="flex items-center text-xs font-semibold text-gray-800"
      >
        <span className="capitalize">{props?.title}</span>
        {props?.required ? (
          <AiFillStar className="ml-1 h-3 w-3 text-red-600" />
        ) : null}
      </label>
      <div className="relative">
        <select
          name={props?.id}
          id={props?.id}
          disabled={props?.disabled}
          onClick={props?.onClick}
          onChange={props?.onChange}
          defaultValue={
            props?.preselect ? String(props?.preselect[0]?.name) : ""
          }
          className={[
            "w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs text-gray-900",
            "shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200",
            props?.disabled ? "opacity-60 cursor-not-allowed" : "",
          ].join(" ")}
        >
          {props?.option?.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
        <AiOutlineCaretDown className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-gray-500" />
      </div>
    </div>
  );
};

/** =========================
 * SelectInput (value-prop API)
 * ========================= */
export const SelectInput = ({
  title,
  value,
  options,
  onClick,
  required,
  disabled,
  onChange,
  defaultValue,
  className,
}) => {
  return (
    <div className="flex flex-col space-y-1.5 text-xs font-semibold text-gray-800">
      <label htmlFor={title} className="flex items-center">
        <span className="capitalize">{title}</span>
        {required ? <AiFillStar className="ml-1 h-3 w-3 text-red-600" /> : null}
      </label>
      <div className="relative">
        <select
          id={title}
          name={title}
          onClick={onClick}
          value={value}
          disabled={disabled}
          onChange={onChange}
          required={required}
          defaultValue={defaultValue}
          className={[
            "w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs text-gray-900",
            "shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200",
            disabled ? "opacity-60 cursor-not-allowed" : "",
            className || "",
          ].join(" ")}
        >
          {options?.map((op, key) => (
            <option key={key} value={String(op)}>
              {op}
            </option>
          ))}
        </select>
        <AiOutlineCaretDown className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-gray-500" />
      </div>
    </div>
  );
};

/** ==============================
 * SearchSelectInput (dropdown)
 * ============================== */
export const SearchSelectInput = ({
  title,
  value,
  options,
  onClick,
  required,
  disabled,
  onChange,
}) => {
  const inputRef = useRef();
  const [dropDown, setDropDown] = useState(false);

  useEffect(() => {
    const handleDropDown = () => {
      if (document.activeElement !== inputRef.current && dropDown) {
        setDropDown(false);
      }
    };
    document.addEventListener("click", handleDropDown);
    return () => document.removeEventListener("click", handleDropDown);
  }, [dropDown, value, document.activeElement, inputRef, options]);

  const optionsFilter = () => {
    if (!value || String(value) === "") return options;
    return options?.filter((x) =>
      String(x).toLowerCase().includes(String(value).toLowerCase()),
    );
  };

  return (
    <div className="relative z-[999] flex flex-col text-xs font-semibold text-gray-800">
      <div
        onClick={() => setDropDown(!dropDown)}
        className="relative w-full space-y-1.5"
      >
        <label htmlFor={title} className="flex items-center">
          <span className="capitalize">{title}</span>
          {required ? (
            <AiFillStar className="ml-1 h-3 w-3 text-red-600" />
          ) : null}
        </label>
        <input
          id={title}
          type="text"
          ref={inputRef}
          onClick={onClick}
          disabled={disabled}
          aria-autocomplete="none"
          autoComplete="off"
          value={value ? value : ""}
          placeholder={`Search or Select ${title}`}
          className={[
            "z-[999] w-full rounded-lg border border-gray-300 px-4 py-2.5 text-xs text-gray-900",
            "placeholder:text-xs placeholder:text-gray-400",
            "shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200",
            "cursor-text",
          ].join(" ")}
          onChange={(e) => {
            setDropDown(true);
            onChange(e.target.value);
          }}
        />
        <AiOutlineCaretDown className="pointer-events-none absolute right-3 top-9 h-4 w-4 text-gray-500" />
      </div>

      {dropDown ? (
        <div className="absolute top-[60px] z-[999] max-h-80 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-2xl">
          {optionsFilter()?.map((op, key) => {
            const active = value === op;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  inputRef?.current?.focus();
                  onChange(op);
                  setDropDown(false);
                }}
                className={[
                  "flex w-full items-center justify-start px-4 py-2 text-left text-xs",
                  active
                    ? "bg-gray-900 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50",
                ].join(" ")}
              >
                {op}
              </button>
            );
          })}
          {optionsFilter()?.length === 0 ? (
            <div className="px-4 py-3 text-left text-xs text-gray-500">
              No results found
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/** =========================
 * UploadButton (file card)
 * ========================= */
export const UploadButton = ({ props }) => {
  return (
    <div className="w-full rounded-2xl border border-emerald-100 bg-white/90 p-5 shadow-sm">
      <p className="text-center text-sm font-semibold uppercase tracking-wide text-gray-900">
        Upload {props.datatype}
      </p>

      <div className="mt-5 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 shadow-inner">
        {(() => {
          if (props.isLoaded && props.id !== "audio") {
            return (
              <div className="flex items-center justify-center">
                <img
                  src={props.file}
                  alt="select thumbnail"
                  className="max-h-[160px] w-auto rounded-lg shadow"
                />
              </div>
            );
          } else if (props.isLoaded && props.id === "audio") {
            return (
              <div className="flex h-[150px] items-center justify-center">
                <MdOutlineCheckCircle className="h-14 w-14 animate-pulse text-emerald-600" />
              </div>
            );
          }
        })()}

        {props.isLoaded === false && (
          <div className="flex items-center justify-center py-6">
            <AiOutlineCloudUpload className="h-16 w-16 text-gray-500" />
          </div>
        )}

        <div
          className={[
            "mt-2 flex flex-col items-center justify-center",
            props.isLoaded === true ? "hidden" : "",
          ].join(" ")}
        >
          <p className="flex items-center justify-center text-sm font-medium text-gray-700">
            Browse to choose a file
            {props.required === true ? (
              <AiFillStar className="ml-1 h-4 w-4 text-red-600" />
            ) : null}
          </p>
          <div className="px-0">
            <input
              id={props.id}
              name={props.id}
              type="file"
              onChange={props.onChange}
              value={props.value}
              accept={!props.format ? ".jpg, .png, .jepg" : ".mp3"}
              className={[
                "file:cursor-pointer file:rounded-lg file:border-0",
                "file:bg-gray-900 file:px-5 file:py-3 file:text-sm file:font-semibold file:uppercase file:text-white",
                "file:shadow-sm hover:file:bg-gray-800",
                "my-4 w-full",
              ].join(" ")}
            />
          </div>
          <p className="text-[11px] text-gray-500">
            JPG/PNG up to 5MB (or MP3 for audio).
          </p>
        </div>
      </div>

      {props.isLoaded === true && (
        <div className="mt-4 w-full">
          <p className="my-2 text-xs font-semibold text-gray-700">Done!</p>
          <div className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm">
            <div className="flex items-center">
              <AiOutlineFileImage className="h-8 w-8 text-gray-500" />
              <div className="mx-3">
                <p className="text-xs text-gray-800">{props.fileName}</p>
                <p className="text-xs text-gray-500">{props.size} Kb</p>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-700 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              onClick={props.closeAction}
              aria-label="Remove file"
            >
              <MdOutlineClose className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** =========================
 * PhoneInput (styled parity)
 * ========================= */
export const PhoneInput = ({
  country,
  setPhone,
  phone = "",
  required = false,
}) => {
  const inputStyle = {
    width: "100%",
    height: "44px",
    paddingTop: "10px",
    paddingBottom: "10px",
    borderColor: "rgb(209,213,219)", // gray-300
    borderRadius: "0.5rem",
    fontSize: "14px",
    boxShadow: "inset 0 1px 1px rgba(0,0,0,0.02)",
  };

  const buttonStyle = {
    height: "44px",
    borderColor: "rgb(229,231,235)", // gray-200
    backgroundColor: "rgb(243,244,246)", // gray-100
    borderTopLeftRadius: "0.5rem",
    borderBottomLeftRadius: "0.5rem",
  };

  let defaultCountry = undefined;

  if (getCountry()) {
    defaultCountry = countriesList?.find(
      (x) =>
        String(x.name).toLowerCase() ===
        String(getCountry()?.country?.name).toLowerCase(),
    )?.code;
  } else if (getOneLevel().length > 0) {
    defaultCountry = countriesList?.find(
      (x) => x.id === getOneLevel()[0]?.data?.data?.country_id,
    )?.code;
  } else {
    defaultCountry = countriesList?.find(
      (x) => x.name === (country || "Uganda"),
    )?.code;
  }

  return (
    <div className="w-full space-y-1.5">
      <label className="flex items-center text-xs font-semibold text-gray-800">
        <span>Phone Number</span>
        {required ? <AiFillStar className="ml-1 h-3 w-3 text-red-600" /> : null}
      </label>
      <div className="flex">
        <CustomInput
          autoFormat={true}
          enableSearch={true}
          preferredCountries={["ug", "cd", "ke"]}
          onlyCountries={[
            "cd",
            "bu",
            "ke",
            "ug",
            "tz",
            "dz",
            "ao",
            "bj",
            "bw",
            "bf",
            "cm",
            "cv",
            "cf",
            "td",
            "km",
            "cg",
            "ci",
            "dj",
            "eg",
            "gq",
            "er",
            "sz",
            "et",
            "ga",
            "gm",
            "gh",
            "gn",
            "gw",
            "ls",
            "lr",
            "ly",
            "mg",
            "mw",
            "ml",
            "mr",
            "mu",
            "ma",
            "mz",
            "na",
            "ne",
            "ng",
            "rw",
            "st",
            "sn",
            "sc",
            "sl",
            "so",
            "za",
            "ss",
            "sd",
            "tg",
            "tn",
            "eh",
            "zm",
            "zw",
          ]}
          inputStyle={inputStyle}
          buttonStyle={buttonStyle}
          enableLongNumbers={false}
          value={phone ? phone : ""}
          onChange={(phone) => setPhone(phone)}
          country={defaultCountry ? defaultCountry : map?.Uganda?.code}
        />
      </div>
    </div>
  );
};

/** =========================
 * TextArea (Jodit wrapper)
 * ========================= */
export const TextArea = ({ props }) => {
  const editor = useRef(null);

  return (
    <div className="relative col-span-1 h-full w-full space-y-1.5 pt-4 xl:col-span-2">
      <label
        htmlFor={props?.id}
        className="flex items-center text-xs font-semibold text-gray-800"
      >
        <span className="capitalize">{props?.title}</span>
        {props?.required === true ? (
          <AiFillStar className="ml-1 h-3 w-3 text-red-600" />
        ) : null}
      </label>

      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <JoditEditor
          ref={editor}
          className="prose prose-sm max-w-none"
          value={props?.value}
          onChange={(newContent) => {
            const e = { target: { value: newContent } };
            return props?.onChange(e);
          }}
        />
      </div>

      <p className="text-[11px] text-gray-500">
        Tip: Use concise, informative descriptions to help travelers quickly
        understand your experience.
      </p>
    </div>
  );
};
